import React, { ChangeEvent } from "react";
import { SMART_LIST_ADAPTER_OPTIONS, SMART_LIST_SORT_OPTIONS } from "../../Api/constants";
import {
    SMART_LIST_GENRE_CHOICES,
    SMART_LIST_TEMPLATE_CHOICES,
} from "../../constants/smartListOptions";
import { SmartListLabelsInput } from "../SmartListLabelsInput";
import { SmartListState } from "./smartListState";

type SmartListFieldsProps = {
    state: SmartListState & {
        setTemplateId: (value: string) => void;
        setAdapterId: (value: number | null) => void;
        setGenre: (value: string) => void;
        setLabels: (value: string[]) => void;
        setSortOption: (value: string) => void;
        requiresGenre: boolean;
        requiresLabels: boolean;
    };
    isSaving: boolean;
    showRequiredLabelWarning: boolean;
};

export const SmartListFields: React.FC<SmartListFieldsProps> = ({
    state,
    isSaving,
    showRequiredLabelWarning,
}) => (
    <>
        <div className="form-group">
            <label>
                Recommendation Engine <span className="field-indicator required">*</span>
            </label>
            <select
                value={state.adapterId?.toString() ?? ""}
                onChange={(event: ChangeEvent<HTMLSelectElement>) =>
                    state.setAdapterId(Number(event.target.value) || null)
                }
                required
                disabled={isSaving}
            >
                <option value="">Select recommendation engine...</option>
                {SMART_LIST_ADAPTER_OPTIONS.map(({ id, name }) => (
                    <option key={id} value={id}>
                        {name}
                    </option>
                ))}
            </select>
        </div>

        {state.adapterId && (
            <div className="form-group">
                <label>
                    Template <span className="field-indicator required">*</span>
                </label>
                <select
                    value={state.templateId}
                    onChange={(event: ChangeEvent<HTMLSelectElement>) =>
                        state.setTemplateId(event.target.value)
                    }
                    required
                    disabled={isSaving}
                >
                    <option value="">Select template...</option>
                    {SMART_LIST_TEMPLATE_CHOICES.map(({ templateId, label }) => (
                        <option key={templateId} value={templateId}>
                            {label}
                        </option>
                    ))}
                </select>
            </div>
        )}

        {state.templateId && (
            <div className="form-group">
                <label>
                    Sort Options <span className="field-indicator required">*</span>
                </label>
                <select
                    value={state.sortOption}
                    onChange={(event: ChangeEvent<HTMLSelectElement>) =>
                        state.setSortOption(event.target.value)
                    }
                    required
                    disabled={isSaving}
                >
                    {SMART_LIST_SORT_OPTIONS.map(({ value, label }) => (
                        <option key={value} value={value}>
                            {label}
                        </option>
                    ))}
                </select>
            </div>
        )}

        {state.requiresGenre && (
            <div className="form-group">
                <label>
                    Genre <span className="field-indicator required">*</span>
                </label>
                <select
                    value={state.genre}
                    onChange={(event: ChangeEvent<HTMLSelectElement>) =>
                        state.setGenre(event.target.value)
                    }
                    required
                    disabled={isSaving}
                >
                    <option value="">Select genre...</option>
                    {SMART_LIST_GENRE_CHOICES.map(({ value, label }) => (
                        <option key={value} value={value}>
                            {label}
                        </option>
                    ))}
                </select>
                <p className="field-helper">Select from the supported Vimond genre tags.</p>
            </div>
        )}

        {state.requiresLabels && (
            <div className="form-group">
                <label>
                    Labels <span className="field-indicator required">*</span>
                </label>
                <SmartListLabelsInput
                    labels={state.labels}
                    onLabelsChange={state.setLabels}
                    helperText="Only one label is allowed per smart list; pressing Enter saves it (adding another replaces the current tag)."
                    maxLabels={1}
                    disabled={isSaving}
                />
                {showRequiredLabelWarning && (
                    <p className="field-helper" style={{ color: "#d23b4b", fontWeight: 500 }}>
                        Add at least one label to save this smart list.
                    </p>
                )}
            </div>
        )}
    </>
);
